import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Switch,
  SxProps,
} from '@mui/material';
import {
  GridApiCommon,
  GridColumnGroup,
  GridColumnGroupingModel,
  GridColumnLookup,
  GridColumnVisibilityModel,
  GridColumnsPanelProps,
  gridColumnLookupSelector,
  gridColumnVisibilityModelSelector,
  isLeaf,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { MutableRefObject, useCallback, useMemo } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { useSxStyles } from '~/components/SolaForceDataGrid/components/GroupColumnsPanel/useStyles';
import { mergeSxProps } from '~/utils/helpers/sxProps';

import {
  filterEnabledGroupsAndFields,
  getColumnGroupLeaves,
  getColumnVisibilityState,
} from './data';

enum ColumnToggleComponent {
  Checkbox = 'checkbox',
  Switch = 'switch',
}

const ToggleComponent = (toggleProps: {
  toggleComponent?: ColumnToggleComponent;
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onChange?: (event: React.SyntheticEvent, checked: boolean) => void;
}) => {
  const { toggleComponent, indeterminate, ...componentProps } = toggleProps;

  const sxStyles = useSxStyles();

  switch (toggleComponent) {
    case ColumnToggleComponent.Checkbox:
      return (
        <Checkbox
          indeterminate={indeterminate}
          {...componentProps}
          size="small"
          sx={sxStyles.toggleComponent}
        />
      );
    case ColumnToggleComponent.Switch:
    default:
      return (
        <Switch
          {...componentProps}
          size="small"
          sx={sxStyles.toggleComponent}
        />
      );
  }
};

const ColumnToggle = (props: {
  field: string;
  checked: boolean;
  disabled?: boolean;
  label: string;
  apiRef: MutableRefObject<GridApiCommon | null>;
}) => {
  const { field, checked, disabled, label, apiRef } = props;

  const toggleColumn = (field: string, checked: boolean) => {
    if (!!apiRef.current) {
      apiRef.current.setColumnVisibility(field, checked);
    }
  };

  return (
    <Stack direction="row" key={field}>
      <FormControlLabel
        control={<ToggleComponent checked={checked} disabled={disabled} />}
        label={label}
        onChange={(_, newValue) => toggleColumn(field, newValue)}
        disabled={disabled}
      />
    </Stack>
  );
};

const ColumnGroup = (props: {
  group: GridColumnGroup;
  columnLookup: GridColumnLookup;
  apiRef: MutableRefObject<GridApiCommon | null>;
  columnVisibilityModel: GridColumnVisibilityModel;
  toggleComponent?: ColumnToggleComponent;
}) => {
  const {
    group,
    columnLookup,
    apiRef,
    columnVisibilityModel,
    toggleComponent,
  } = props;

  const sxStyles = useSxStyles();

  const leaves = useMemo(() => {
    const fields: string[] = [];
    getColumnGroupLeaves(group, (field) => fields.push(field));
    return fields;
  }, [group]);

  const isFieldHideDisabled = useCallback(
    (field: string) => {
      const hideable = columnLookup[field]?.hideable;
      return hideable === false;
    },
    [columnLookup],
  );

  const { isGroupChecked, isGroupIndeterminate } = useMemo(() => {
    const { isColumnHidden, isColumnVisible } = getColumnVisibilityState(
      columnVisibilityModel,
    );

    if (toggleComponent === ColumnToggleComponent.Checkbox) {
      return {
        isGroupChecked: leaves.every(isColumnVisible),
        isGroupIndeterminate:
          leaves.some(isColumnHidden) && !leaves.every(isColumnHidden),
      };
    }

    return {
      isGroupChecked: leaves.some(
        (field) => isColumnVisible(field) && !isFieldHideDisabled(field),
      ),
      isGroupIndeterminate: false,
    };
  }, [columnVisibilityModel, leaves, toggleComponent, isFieldHideDisabled]);

  const toggleColumnGroup = (checked: boolean) => {
    const newColumnVisibilityModel = {
      ...columnVisibilityModel,
    };
    getColumnGroupLeaves(group, (field) => {
      newColumnVisibilityModel[field] = isFieldHideDisabled(field)
        ? true
        : checked;
    });
    if (!!apiRef.current) {
      apiRef.current.setColumnVisibilityModel(newColumnVisibilityModel);
    }
  };

  return (
    <Box>
      <FormControlLabel
        control={
          <ToggleComponent
            checked={isGroupChecked}
            indeterminate={isGroupIndeterminate}
          />
        }
        label={group.headerName ?? group.groupId}
        onChange={(_, newValue) => toggleColumnGroup(newValue)}
      />
      <Box sx={sxStyles.groupList}>
        {group.children.map((child) => {
          if (isLeaf(child)) {
            return (
              <ColumnToggle
                key={child.field}
                field={child.field}
                checked={getColumnVisibilityState(
                  columnVisibilityModel,
                ).isColumnVisible(child.field)}
                disabled={isFieldHideDisabled(child.field)}
                label={columnLookup[child.field]?.headerName ?? child.field}
                apiRef={apiRef}
              />
            );
          }
          return (
            <ColumnGroup
              group={child}
              columnLookup={columnLookup}
              key={child.groupId}
              apiRef={apiRef}
              columnVisibilityModel={columnVisibilityModel}
              toggleComponent={toggleComponent}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export const GroupColumnsPanelContent = (props: {
  columnGroupingModel?: GridColumnGroupingModel;
  apiRef: MutableRefObject<GridApiCommon>;
  toggleComponent?: ColumnToggleComponent;
  sx?: SxProps;
}) => {
  const { toggleComponent, columnGroupingModel, apiRef, sx } = props;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const columnLookup = useGridSelector(apiRef, gridColumnLookupSelector);
  const columnVisibilityModel = useGridSelector(
    apiRef,
    gridColumnVisibilityModelSelector,
  );

  const allColumns = !!apiRef.current?.getAllColumns
    ? apiRef.current.getAllColumns()
    : [];

  const filteredColumnGroupingModel =
    filterEnabledGroupsAndFields(columnLookup, columnGroupingModel) ?? [];

  const toggleAllColumns = (checked: boolean) => {
    const newColumnVisibilityModel: GridColumnVisibilityModel =
      Object.fromEntries(
        allColumns.map(({ field, hideable }) => {
          const state = hideable === false ? true : checked;
          return [field, state];
        }),
      );

    if (!!apiRef.current) {
      apiRef.current.setColumnVisibilityModel(newColumnVisibilityModel);
    }
  };

  return (
    <Box sx={mergeSxProps(sxStyles.root, sx)}>
      <Box sx={sxStyles.listContainer}>
        {!filteredColumnGroupingModel ||
        filteredColumnGroupingModel.length === 0
          ? allColumns.map((column) => {
              const { field, headerName, hideable } = column;

              return (
                <ColumnToggle
                  key={field}
                  field={field}
                  checked={getColumnVisibilityState(
                    columnVisibilityModel,
                  ).isColumnVisible(field)}
                  disabled={hideable === false}
                  label={headerName ?? field}
                  apiRef={apiRef}
                />
              );
            })
          : filteredColumnGroupingModel.map((group) => (
              <ColumnGroup
                group={group}
                columnLookup={columnLookup}
                columnVisibilityModel={columnVisibilityModel}
                key={group.groupId}
                apiRef={apiRef}
                toggleComponent={toggleComponent}
              />
            ))}
      </Box>
      <Box sx={sxStyles.buttonContainer}>
        <Button onClick={() => toggleAllColumns(false)}>{t('hideAll')}</Button>
        <Button onClick={() => toggleAllColumns(true)}>{t('showAll')}</Button>
      </Box>
    </Box>
  );
};

export const GroupColumnsPanel = (
  props: GridColumnsPanelProps & {
    toggleComponent?: ColumnToggleComponent;
  },
) => {
  const { toggleComponent } = props;

  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();

  return (
    <GroupColumnsPanelContent
      apiRef={apiRef}
      columnGroupingModel={rootProps.columnGroupingModel}
      toggleComponent={toggleComponent}
    />
  );
};
